// Runner positions → DOM (7.6F.1, ADR-0023; framework-free like animate.ts;
// moved from the Studio to @interactads/creative-runtime in 7.6I so the served
// ad paints the SAME way as the preview). The VALUES come from the Creative
// Engine's RunnerSession (fixed-step simulation); this module only writes them
// imperatively each frame, so a running game never re-renders React per frame.
//
// Composition with authored animation and states (one owner per channel):
//   runner      → `left` / `top` (where the layer is: scrolling, the player's jump)
//                 and a multiplier of `opacity` (segment scenery cross-fade)
//   animation   → `transform` / `opacity` (offsets, scale, rotation relative to it)
//   state       → `translate` / `scale` / `rotate` / `filter` (runtime-state.ts)
// So a hit shake, a pickup pop or a fade of the player composes with its jump
// instead of fighting over the same geometry. Call it AFTER applySampledStyles.

import type { RunnerSession } from "@interactads/creative-model";

// Element ids are restricted by the Creative Document (ID_PATTERN: letters, digits, `_` and `-`),
// so they are safe inside an attribute selector without escaping.

const px = (v: number) => `${String(Math.round(v * 100) / 100)}px`;

export function paintRunner(root: ParentNode | null, runner: RunnerSession | null | undefined): void {
  if (!root || !runner) return;
  const decl = runner.decl;
  const byId = (id: string) => root.querySelector<HTMLElement>(`[data-element-id="${id}"]`);

  const player = byId(decl.player.element);
  if (player) {
    player.style.top = px(runner.playerY());
    player.dataset.runnerPose = runner.pose;
  }

  for (const l of decl.layers ?? []) {
    const p = runner.layerX(l.element);
    const node = byId(l.element);
    if (!p || !node) continue;
    node.style.left = px(p.x);
    const copy = root.querySelector<HTMLElement>(`[data-runner-copy="${l.element}"]`);
    if (copy && p.copy !== undefined) copy.style.left = px(p.copy);
  }

  for (const item of decl.items ?? []) {
    const node = byId(item.element);
    if (!node) continue;
    const x = runner.itemX(item.element);
    if (x === undefined) node.style.visibility = "hidden";
    else node.style.left = px(x);
  }

  // Segment scenery: authored opacity (already written this frame) × the cross-fade.
  const scenery = new Set(decl.segments.flatMap((s) => s.show));
  for (const id of scenery) {
    const factor = runner.sceneryOpacity(id) ?? 1;
    const node = byId(id);
    const copy = root.querySelector<HTMLElement>(`[data-runner-copy="${id}"]`);
    if (!node) continue;
    // The animation driver rewrote the layer's opacity/visibility this frame; the copy follows the layer.
    const authored = Number(node.style.opacity === "" ? 1 : node.style.opacity);
    node.style.opacity = String(Math.round(authored * factor * 10_000) / 10_000);
    if (factor === 0) node.style.visibility = "hidden";
    if (copy) {
      copy.style.opacity = node.style.opacity;
      copy.style.visibility = node.style.visibility;
    }
  }

  // Spawned instances: each lives in a stable slot of its template's pool.
  const live = new Map(runner.instances().map((i) => [`${i.template}#${String(i.slot)}`, i]));
  for (const slot of root.querySelectorAll<HTMLElement>("[data-runner-slot]")) {
    const inst = live.get(slot.dataset.runnerSlot ?? "");
    if (!inst) {
      if (slot.style.display !== "none") slot.style.display = "none";
      continue;
    }
    slot.style.display = "";
    slot.style.left = px(inst.x);
    slot.style.top = px(inst.y);
    slot.dataset.runnerKind = inst.kind;
  }
}
